/**
 * The drawings an empty card carries — one per thing that is missing.
 *
 * ── Why an empty card gets a drawing at all ───────────────────────────────────────────────────
 * Owner, 2 September 2026: "puste karty mają mieć ładne infografiki, tak jak na mobilce często
 * mają małe ikonki, a nie sam tekst." A card that says only "Brak aktywnego celu." reads as a card
 * that failed rather than a card with an answer, and the sentence carries the whole weight of
 * telling the trainer which of the six cards on the screen just spoke.
 *
 * ── Provenance: transcribed from the MOBILE app's glyph registry ──────────────────────────────
 * `move-with-me-frontend/move-with-me/src/design-system/primitives/Icon.glyphs.tsx` is that app's
 * one icon set, and `docs/design/README.md` names it as the set this app ships too. Every path
 * below is copied from it character for character, under its own registry name, so the trainer
 * meets one drawing per concept on both platforms — the same rule `CalendarEventIcons` follows and
 * for the same reason. Nothing here is invented, and nothing is an emoji: the ban is absolute.
 *
 * The construction is this repo's house one, shared with `AuthIcons`, `RecordIcons` and
 * `CalendarEventIcons`: a 24×24 box, `fill: none`, `currentColor` at a 2px round stroke,
 * `aria-hidden`. The colour is the CALLER's — `EmptyState` sets it from the tone — and the size is
 * the caller's too, so one drawing serves a 28px card mark and a 16px inline one.
 *
 * ── Every one of them is `aria-hidden`, always ────────────────────────────────────────────────
 * The sentence beside the drawing is what says what is missing. A glyph that repeated it would say
 * it twice, and a glyph that replaced it would be meaning carried in a picture alone.
 */

import { motion } from 'motion/react';

import type { ScreenMotionStep } from '~/shared/design-system/motion';

interface IconProps {
  className?: string;
}

const STROKE_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/**
 * Mobile's `check2`, a check inside a circle — its own "Sesja ukończona" mark. Nothing is waiting.
 *
 * ── `check` ───────────────────────────────────────────────────────────────────────────────────
 * The one drawing in this file whose two parts can arrive separately, because one surface earns it
 * in front of the viewer: the trainer's home writes the check into an already-drawn circle the
 * moment the last task of the morning is confirmed closed. Omitted — every other caller — the
 * check is simply there, which is what a state that was already true looks like.
 */
export function DoneMarkIcon({ className, check }: DoneMarkIconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <circle cx="12" cy="12" r="9" />
      {check === false ? null : <motion.path d="m8.5 12.4 2.5 2.6 4.6-5" {...check} />}
    </svg>
  );
}

interface DoneMarkIconProps extends IconProps {
  /**
   * How the check inside the circle arrives: a motion step writes it stroke-first, `false` leaves
   * the circle empty for a caller that has not earned it yet, and omitting it draws it.
   *
   * `false` rather than a named state. This is the registry of marks every surface shares, and the
   * one caller that leaves the circle empty is the queue card waiting on a write — naming the
   * absence `'waiting'` put that card's vocabulary into a drawing the roster and the plan library
   * also use, for a distinction only one of them can make.
   */
  readonly check?: ScreenMotionStep | false;
}

/** Mobile's `plan` — the document with a calendar tab it marks "Przypisz nowy plan" with. */
export function PlanMarkIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="4" y="3" width="16" height="18" rx="2.5" />
      <path d="M8 3v3M16 3v3M8 11h8M8 15h5" />
    </svg>
  );
}

/** Mobile's `target` — its coaching-goal marker. The centre dot is filled, as it is there. */
export function GoalMarkIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Mobile's `dumbbell` — the glyph it marks the "Ciężar" record with. A day with no exercises in it.
 *
 * Transcribed rather than authored, like everything else here: the first draft of this drawing was
 * an invented flat dumbbell, which broke the one rule this file has. Mobile's set already answers
 * "weight lifted", and a trainer who has met that drawing on the phone meets the same one here.
 */
export function DumbbellMarkIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M6.5 6.5 17.5 17.5" />
      <path d="m21 21-1-1" />
      <path d="m3 3 1 1" />
      <path d="m18 22 4-4" />
      <path d="m2 6 4-4" />
      <path d="m3 10 7-7" />
      <path d="m14 21 7-7" />
    </svg>
  );
}

/** Mobile's `video` — a camera. A client who has not filmed anything yet. */
export function VideoMarkIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect height="12" rx="2.5" width="13" x="2.5" y="6" />
      <path d="m15.5 10 6-3.5v11L15.5 14z" />
    </svg>
  );
}

/** Mobile's `note` — the folded-corner page it marks a trainer note with. */
export function NoteMarkIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M4 4h16v12l-4 4H4z" />
      <path d="M16 20v-4h4" />
      <path d="M8 9h8M8 13h5" />
    </svg>
  );
}

/** Mobile's `ruler` — its "measurement" glyph, on the client-activity row and here. */
export function MeasureMarkIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="3" y="7" width="18" height="10" rx="1" />
      <path d="M7 7v3M11 7v4M15 7v3M19 7v4" />
    </svg>
  );
}

/** Mobile's `cal` — a plain calendar. A window with nothing booked in it. */
export function CalendarMarkIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="3" y="4" width="18" height="18" rx="2.5" />
      <path d="M8 2v4M16 2v4M3 10h18" />
    </svg>
  );
}

/** Mobile's `bars` — its "Objętość" mark. A volume that was never registered. */
export function VolumeMarkIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="4" y="13" width="4" height="7" rx="1" />
      <rect x="10" y="9" width="4" height="11" rx="1" />
      <rect x="16" y="5" width="4" height="15" rx="1" />
    </svg>
  );
}

/** Mobile's `prE1rm` — a rising line with a corner tick. A series with no points in it. */
export function TrendMarkIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="m3 16 5-5 4 3 6-7" />
      <path d="M19 4h2v2" />
    </svg>
  );
}

/** Mobile's `inbox` — an open tray. A list of past coaching relationships with nothing in it. */
export function InboxMarkIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.5 5.5 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.5-6.5A2 2 0 0 0 16.7 4H7.3a2 2 0 0 0-1.8 1.5z" />
    </svg>
  );
}

/** Mobile's `clock` — its outline clock. A stretch of time with no finished session in it. */
export function ElapsedMarkIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5V12l3.5 2" />
    </svg>
  );
}

/** Credits: two coins, the front one struck (owner 27.09 over the T-C8 canon's two rings). */
export function CreditMarkIcon({ className }: IconProps) {
  // Two coins, the front one struck with its value — never concentric circles, which read as the
  // goal's target (owner, 27 September 2026).
  return (
    <svg className={className} {...STROKE_PROPS}>
      <circle cx="9" cy="9" r="6" />
      <path d="M15 9a6 6 0 1 1-6 6" />
      <path d="M8 7.5l1-1v5" />
    </svg>
  );
}
